import { useEffect, useState } from "react";
import { LABEL_ADULTS, LABEL_CHILDREN_FREE, LABEL_CHILDREN_HALF } from "../../shared/child-price";
import { formatMoney } from "../../shared/money";
import { api } from "../api";
import { Banner, messageOf } from "./ui";

type Card = {
  id: string;
  name: string;
  charge_role: string;
  guest_band: string;
  season_label: string | null;
  days_of_week: string | null;
  selling_price_minor: number | null;
  internal_cost_minor: number | null;
  valid_from: string | null;
  valid_to: string | null;
  venue_id: string;
};

type Venue = { id: string; name: string };
type Package = { id: string; code: string; name_en: string };
type Quote = {
  lines: { id: string; description: string; quantity_milli: number; unit_price_minor: number; taxable_net_minor: number; internal_cost_minor: number | null; explanation: string; client_visible: boolean }[];
  totals: { net_minor: number; tax_minor: number | null; gross_minor: number | null; currency: string };
  explanations: string[];
  issues: { severity: string; message: string }[];
  availability: { state: string; detail: string };
  margin: { status: string; margin_minor: number | null };
  options: { key: string; label: string; totals: { net_minor: number } }[];
};

export function RatesPage() {
  const [cards, setCards] = useState<Card[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [venueId, setVenueId] = useState("ven_kolonaki");
  const [date, setDate] = useState("2026-10-14");
  const [adults, setAdults] = useState("40");
  const [children, setChildren] = useState("0");
  const [childrenFree, setChildrenFree] = useState("");
  const [packageId, setPackageId] = useState("");

  useEffect(() => {
    Promise.all([
      api<{ rate_cards: Card[] }>("/api/rate-cards"),
      api<{ venues: Venue[] }>("/api/venues"),
      api<{ packages: Package[] }>("/api/catalogue"),
    ]).then(([rates, venueList, catalogue]) => {
      setCards(rates.rate_cards);
      setVenues(venueList.venues);
      setPackages(catalogue.packages);
    }).catch((reason: unknown) => setError(messageOf(reason)));
  }, []);

  async function preview() {
    setError(null);
    try {
      const result = await api<{ quote: Quote }>("/api/quote/preview", {
        method: "POST",
        body: JSON.stringify({
          venue_id: venueId,
          date,
          adults: adults.trim() ? Number(adults) : null,
          children: children.trim() ? Number(children) : null,
          children_free: childrenFree.trim() ? Number(childrenFree) : null,
          package_id: packageId || null,
        }),
      });
      setQuote(result.quote);
    } catch (reason) {
      setError(messageOf(reason));
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Τιμές και κόστη</h1>
          <p className="lede">Η τιμή πελάτη και το εσωτερικό κόστος αποθηκεύονται χωριστά. Η σεζόν και η ημέρα διαλέγουν έναν κανόνα ενηλίκων. Δεν προστίθενται. Ισοπαλία σταματά την προσφορά ώσπου κάποιος να διαλέξει.</p>
        </div>
      </header>
      <Banner error={error} />
      <div className="panel" style={{ overflow: "auto" }}>
        <table>
          <thead>
            <tr><th>Τιμή</th><th>Ρόλος</th><th>Πότε</th><th className="num">Πελάτης</th><th className="num">Εσωτερικό κόστος</th></tr>
          </thead>
          <tbody>
            {cards.map((card) => (
              <tr key={`${card.id}-${card.season_label ?? ""}-${card.days_of_week ?? ""}`}>
                <td>{card.name}</td>
                <td>{card.charge_role}</td>
                <td>{card.season_label || card.days_of_week || "Οποιαδήποτε μέρα"}{card.valid_from ? ` · ${card.valid_from} έως ${card.valid_to}` : ""}</td>
                <td className="num">{card.selling_price_minor == null ? "—" : formatMoney(card.selling_price_minor)}</td>
                <td className="num">{card.internal_cost_minor == null ? "Αγνωστο" : formatMoney(card.internal_cost_minor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {cards.length === 0 ? <p>Δεν υπάρχουν δημοσιευμένες τιμές ακόμη.</p> : null}
      </div>
      <section className="panel" style={{ marginTop: 16 }}>
        <h2>Προσφορά από τους ζωντανούς κανόνες</h2>
        <div className="fields">
          <label>Χώρος
            <select value={venueId} onChange={(event) => setVenueId(event.target.value)}>
              {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
            </select>
          </label>
          <label>Ημερομηνία<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label>{LABEL_ADULTS}<input value={adults} onChange={(event) => setAdults(event.target.value)} inputMode="numeric" /></label>
          <label>{LABEL_CHILDREN_HALF}<input value={children} onChange={(event) => setChildren(event.target.value)} inputMode="numeric" /></label>
          <label>{LABEL_CHILDREN_FREE}<input value={childrenFree} onChange={(event) => setChildrenFree(event.target.value)} inputMode="numeric" /></label>
          <label>Πακέτο
            <select value={packageId} onChange={(event) => setPackageId(event.target.value)}>
              <option value="">Σύγκριση basic, signature, premium</option>
              {packages.map((item) => <option key={item.id} value={item.id}>{item.name_en}</option>)}
            </select>
          </label>
        </div>
        <button type="button" onClick={() => void preview()}>Υπολογισμός</button>
        <p className="meta">Δοκίμασε 14 Οκτωβρίου 2026 για καθημερινή, 17 Οκτωβρίου για Σαββατοκύριακο και 2 Δεκεμβρίου 2026 για χειμώνα. Αυτά είναι ενδεικτικά νούμερα.</p>
        {quote ? (
          <>
            <p className={`pill ${quote.availability.state}`}>{quote.availability.state}</p>
            <p>{quote.availability.detail}</p>
            {quote.options.length > 0 ? (
              <p>Επιλογές: {quote.options.map((option) => `${option.label} ${formatMoney(option.totals.net_minor)} καθαρά`).join(" · ")}</p>
            ) : null}
            <table>
              <thead><tr><th>Γραμμή</th><th className="num">Ποσ.</th><th className="num">Τιμή</th><th className="num">Καθαρά</th><th className="num">Κόστος</th></tr></thead>
              <tbody>
                {quote.lines.filter((line) => line.client_visible).map((line) => (
                  <tr key={line.id}>
                    <td>{line.description}<div className="meta">{line.explanation}</div></td>
                    <td className="num">{line.quantity_milli / 1000}</td>
                    <td className="num">{formatMoney(line.unit_price_minor)}</td>
                    <td className="num">{formatMoney(line.taxable_net_minor)}</td>
                    <td className="num">{line.internal_cost_minor == null ? "Αγνωστο" : formatMoney(line.internal_cost_minor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>Καθαρά {formatMoney(quote.totals.net_minor)} · φόρος {quote.totals.tax_minor == null ? "εκκρεμεί" : formatMoney(quote.totals.tax_minor)} · μικτά {quote.totals.gross_minor == null ? "εκκρεμεί" : formatMoney(quote.totals.gross_minor)}</p>
            <p>Περιθώριο {quote.margin.status}{quote.margin.margin_minor == null ? "" : ` ${formatMoney(quote.margin.margin_minor)}`}</p>
            {quote.issues.map((issue) => <p key={issue.message} className="banner">{issue.severity}: {issue.message}</p>)}
            <ul>{quote.explanations.slice(0, 6).map((line) => <li key={line}>{line}</li>)}</ul>
          </>
        ) : null}
      </section>
    </>
  );
}
